import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SupportSessionInfo, SupportSessionStart, TenantClosure, TenantClosureRequest, TenantExport, TenantOverview } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatBytes, formatDateTime, formatNumber } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Badge, Button, Card, ConfirmButton, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';

const postExport = (tenantId: string) => api.post(`/platform/tenants/${tenantId}/exports`, { schema: TenantExport }) as Promise<TenantExport>;

export const overviewKey = (tenantId: string) => ['platform', 'tenants', tenantId, 'overview'] as const;

export function useTenantOverview(tenantId: string) {
  return useQuery({
    queryKey: overviewKey(tenantId),
    queryFn: () => api.get(`/platform/tenants/${tenantId}/overview`, { schema: TenantOverview }),
  });
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <dt className="text-xs text-slate-600">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

const HEALTH_TONE = { ok: 'good', warning: 'warn', problem: 'bad' } as const;

/** Tenant overview tiles (core-phase4 s6, GET /platform/tenants/:id/overview). */
export function TenantOverviewSection({ tenantId }: { tenantId: string }) {
  const q = useTenantOverview(tenantId);
  return (
    <Card title={t('partner.overview.title')} description={t('partner.overview.description')}>
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data ? (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile label={t('partner.overview.sites')} value={formatNumber(q.data.sites)} />
            <Tile label={t('partner.overview.kiosks')} value={formatNumber(q.data.kiosks)} />
            <Tile label={t('partner.overview.kiosksOffline')} value={formatNumber(q.data.kiosksOffline)} />
            <Tile label={t('partner.overview.users')} value={formatNumber(q.data.users)} />
          </dl>
          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="font-medium">{t('partner.overview.health')}</dt>
              <dd>
                <Badge tone={HEALTH_TONE[q.data.health]}>{t(`partner.health.${q.data.health}`)}</Badge>
              </dd>
            </div>
            <div>
              <dt className="font-medium">{t('partner.overview.restriction')}</dt>
              <dd>{t(`partner.restriction.${q.data.restriction}`)}</dd>
            </div>
            <div>
              <dt className="font-medium">{t('partner.overview.supportAllowed')}</dt>
              <dd>{q.data.supportAccessAllowed ? t('partner.overview.yes') : t('partner.overview.no')}</dd>
            </div>
            <div>
              <dt className="font-medium">{t('partner.overview.usage')}</dt>
              <dd>
                <ul>
                  {q.data.usage.map((u) => (
                    <li key={u.metric}>
                      {t(`partner.usage.${u.metric}`)}: {formatNumber(u.value)}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="font-medium">{t('partner.overview.domains')}</dt>
              <dd>
                {q.data.domains.length === 0 ? (
                  <span className="text-slate-600">{t('partner.overview.noDomains')}</span>
                ) : (
                  <ul className="font-mono">
                    {q.data.domains.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
          </dl>
        </div>
      ) : null}
    </Card>
  );
}

/** Start a support session (core-phase4 s3). On success the session is in the customer's portal: reload capabilities and go home. */
export function TenantSupportSection({ tenantId, active }: { tenantId: string; active: boolean }) {
  const can = useCan('platform.support_access');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState('30');
  const start = useMutation({
    mutationFn: () => {
      const body = SupportSessionStart.parse({ reason, minutes: Number(minutes) });
      return api.post(`/platform/tenants/${tenantId}/support-session`, { body, schema: SupportSessionInfo });
    },
    onSuccess: async () => {
      await qc.invalidateQueries();
      void navigate('/');
    },
  });
  if (!can) return null;
  const errs = fieldErrors(start.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    start.mutate();
  };
  return (
    <Card title={t('partner.support.title')} description={t('partner.support.description')}>
      {!active ? <Alert kind="info">{t('partner.support.unavailable')}</Alert> : null}
      <form onSubmit={submit} className="mt-2 grid max-w-xl gap-4">
        <ErrorLine error={start.error} />
        <TextField
          label={t('partner.support.reason')}
          hint={t('partner.support.reasonHint')}
          required
          minLength={5}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={errs['reason']}
        />
        <TextField
          label={t('partner.support.minutes')}
          hint={t('partner.support.minutesHint')}
          type="number"
          min={5}
          max={240}
          required
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          error={errs['minutes']}
        />
        <Button type="submit" variant="primary" className="self-start" disabled={start.isPending || !active || reason.trim().length < 5}>
          {t('partner.support.start')}
        </Button>
      </form>
    </Card>
  );
}

/** Tenant export: request, then follow its status until a download link is ready. */
export function TenantExportSection({ tenantId }: { tenantId: string }) {
  const canManage = useCan('platform.tenants_manage');
  const [exportId, setExportId] = useState<string | null>(null);
  const request = useMutation({
    mutationFn: () => postExport(tenantId),
    onSuccess: (e) => setExportId(e.id),
  });
  const status = useQuery({
    queryKey: ['platform', 'tenants', tenantId, 'exports', exportId],
    enabled: exportId !== null,
    queryFn: () => api.get(`/platform/tenants/${tenantId}/exports/${exportId}`, { schema: TenantExport }),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'queued' || s === 'running' || s === undefined ? 5000 : false;
    },
  });
  const exp = status.data ?? request.data;
  return (
    <Card title={t('partner.export.title')} description={t('partner.export.description')}>
      <ErrorLine error={request.error} />
      {status.isError ? <ErrorPanel error={status.error} onRetry={() => void status.refetch()} /> : null}
      {canManage ? (
        <Button variant="secondary" disabled={request.isPending} onClick={() => request.mutate()}>
          {t('partner.export.request')}
        </Button>
      ) : null}
      {exp ? (
        <div className="mt-4 space-y-2 text-sm" role="status" aria-live="polite">
          <Badge tone={exp.status === 'ready' ? 'good' : exp.status === 'failed' ? 'bad' : 'neutral'}>{t(`partner.export.status.${exp.status}`)}</Badge>
          <p>{t('partner.export.requested', { when: formatDateTime(exp.requestedAt) })}</p>
          {exp.sizeBytes !== null ? <p>{t('partner.export.size', { size: formatBytes(exp.sizeBytes) })}</p> : null}
          {exp.status === 'ready' && exp.downloadUrl ? (
            <>
              {exp.expiresAt ? <p>{t('partner.export.expires', { when: formatDateTime(exp.expiresAt) })}</p> : null}
              <a className="inline-block font-medium text-brand hover:underline" href={exp.downloadUrl}>
                {t('partner.export.download')}
              </a>
            </>
          ) : null}
          {exp.status === 'ready' && !exp.downloadUrl ? (
            <Button variant="secondary" onClick={() => void status.refetch()}>
              {t('partner.export.refresh')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/** Closure: typed slug and reason to book it; cancel while it is pending. */
export function TenantClosureSection({ tenantId, slug }: { tenantId: string; slug: string }) {
  const canManage = useCan('platform.tenants_manage');
  const qc = useQueryClient();
  const overview = useTenantOverview(tenantId);
  const [confirmSlug, setConfirmSlug] = useState('');
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
  };
  const book = useMutation({
    mutationFn: () => {
      const body = TenantClosureRequest.parse({ confirmSlug, reason, ...(days.trim() ? { noticeDays: Number(days) } : {}) });
      return api.post(`/platform/tenants/${tenantId}/closure`, { body, schema: TenantClosure });
    },
    onSuccess: async () => {
      setConfirmSlug('');
      setReason('');
      setDays('');
      setNotice(t('partner.closure.booked'));
      await refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: () => api.delete(`/platform/tenants/${tenantId}/closure`),
    onSuccess: async () => {
      setNotice(t('partner.closure.cancelled'));
      await refresh();
    },
  });
  if (!canManage) return null;
  const pending = overview.data?.closure ?? null;
  const errs = fieldErrors(book.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setNotice(null);
    book.mutate();
  };
  return (
    <Card title={t('partner.closure.title')} description={t('partner.closure.description')}>
      {notice ? <Alert kind="success">{notice}</Alert> : null}
      <ErrorLine error={cancel.error} />
      {pending ? (
        <div className="space-y-3">
          <Alert kind="info">{t('partner.closure.pending', { when: formatDateTime(pending.deleteAfter) })}</Alert>
          <Button variant="secondary" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            {t('partner.closure.cancel')}
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="grid max-w-xl gap-4">
          <ErrorLine error={book.error} />
          <TextField
            label={t('partner.closure.confirmSlug', { slug })}
            value={confirmSlug}
            autoComplete="off"
            onChange={(e) => setConfirmSlug(e.target.value)}
            error={errs['confirmSlug']}
          />
          <TextField label={t('partner.closure.reason')} required maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} error={errs['reason']} />
          <TextField
            label={t('partner.closure.noticeDays')}
            hint={t('partner.closure.noticeHint')}
            type="number"
            min={7}
            max={365}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            error={errs['noticeDays']}
          />
          <ConfirmButton
            label={t('partner.closure.start')}
            pending={book.isPending}
            disabled={confirmSlug !== slug || !reason.trim()}
            onConfirm={() => book.mutate()}
          />
        </form>
      )}
    </Card>
  );
}
